import { isPlainObject } from 'lodash-es';

import type {
  IAnalysisAxisChartDataDto,
  IAnalysisAxisChartDataVo,
  IAnalysisChartDisplayMetaVo,
  IAnalysisChartResponseDtoMap,
  IAnalysisChartResponseMetaDto,
  IAnalysisChartSeriesItemDto,
  IAnalysisColumnHeaderDto,
  IAnalysisColumnHeaderVo,
  TAnalysisChartMapKey,
  TAnalysisChartResponseDtoOf,
  TAnalysisTableRowVo,
} from '../types';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

/**
 * 非法 wire 兜底空 Dto（字段由 transform `isNoData` 路径消费）。
 *
 * 仅在本文件 boundary 使用一次类型断言；对外 API 不出现 `unknown`。
 */
function emptyAnalysisChartResponseDto<
  K extends TAnalysisChartMapKey,
>(): TAnalysisChartResponseDtoOf<K> {
  const emptyDto: Partial<TAnalysisChartResponseDtoOf<K>> = {};
  return emptyDto as TAnalysisChartResponseDtoOf<K>;
}

/**
 * Response Dto runtime 校验：Dispatcher 已断言为 Dto，此处仅拦截非 plain object 的异常 wire。
 */
export function normalizeResponseDto<K extends TAnalysisChartMapKey>(
  data: TAnalysisChartResponseDtoOf<K>,
): TAnalysisChartResponseDtoOf<K> {
  if (!isPlainObject(data)) {
    return emptyAnalysisChartResponseDto<K>();
  }
  return data;
}

export function normalizeStringArray(
  value: string[] | null | undefined,
): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];
}

function normalizeColumnHeaderItem(
  item: IAnalysisColumnHeaderDto | null | undefined,
): IAnalysisColumnHeaderVo | undefined {
  if (!isRecord(item)) {
    return undefined;
  }

  const {
    name,
    name_en: nameEn,
    origin_name: originName,
    can_sort: canSort,
    type,
    column_date_type: columnDateType,
    table_code: tableCode,
    table_id: tableId,
    table_name: tableName,
    aggregate,
    accuracy,
    unit,
    attribute,
    customize_name: customizeName,
    customize_name_en: customizeNameEn,
    is_compare_date: isCompareDate,
    is_user_filter: isUserFilter,
    is_sensitive: isSensitive,
  } = item;

  if (typeof name !== 'string') {
    return undefined;
  }

  return {
    name,
    nameEn: typeof nameEn === 'string' ? nameEn : undefined,
    originName: typeof originName === 'string' ? originName : undefined,
    canSort: typeof canSort === 'boolean' ? canSort : undefined,
    type: typeof type === 'number' ? type : undefined,
    columnDateType:
      typeof columnDateType === 'string' ? columnDateType : undefined,
    tableCode: typeof tableCode === 'string' ? tableCode : undefined,
    tableId: typeof tableId === 'string' ? tableId : undefined,
    tableName: typeof tableName === 'string' ? tableName : undefined,
    aggregate: typeof aggregate === 'string' ? aggregate : undefined,
    accuracy: typeof accuracy === 'number' ? accuracy : undefined,
    unit: unit === null || typeof unit === 'string' ? unit : undefined,
    attribute: typeof attribute === 'number' ? attribute : undefined,
    customizeName:
      typeof customizeName === 'string' ? customizeName : undefined,
    customizeNameEn:
      typeof customizeNameEn === 'string' ? customizeNameEn : undefined,
    isCompareDate:
      typeof isCompareDate === 'boolean' ? isCompareDate : undefined,
    isUserFilter: typeof isUserFilter === 'boolean' ? isUserFilter : undefined,
    isSensitive: typeof isSensitive === 'boolean' ? isSensitive : undefined,
  };
}

export function normalizeColumnHeaderList(
  value: IAnalysisColumnHeaderDto[] | null | undefined,
): IAnalysisColumnHeaderVo[] {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .map(normalizeColumnHeaderItem)
    .filter((item): item is IAnalysisColumnHeaderVo => item !== undefined);
}

/**
 * `table_data` 透传：仅 `null`/非数组兜底 `[]`，不做逐行/逐格 Vo 映射（大数据量性能）。
 */
export function normalizeTableDataList(value: TAnalysisTableRowVo[] | null | undefined): TAnalysisTableRowVo[] {
  // value 正常应该是TAnalysisTableRowVo[]类型, 但是value是后端返回的值, 不确定后端会不会按照契约返回, 所以这里兜底一下.
  if (!Array.isArray(value)) {
    return [];
  }

  return value as TAnalysisTableRowVo[];
}

/** 已通过 {@link isAxisChartWireDto} 校验的轴类 wire 块（轴/序列均为非空数组）。 */
type TAxisChartWireDto = IAnalysisAxisChartDataDto & {
  x_axis_data: string[];
  series_data: IAnalysisChartSeriesItemDto[];
};

/**
 * Vo 无数据态 · payload omit 约定（各 chartType transform 共用）。
 *
 * `isNoData === true` 时：**不**调用 chart 私有 `normalize*`，也**不**在 Result Vo 上挂载图表 payload，
 * 通过 `...(isNoData ? {} : { barChart })` 省略字段（属性不存在 ≡ `undefined`）。
 *
 * ## 可选兜底值对比（为何 Vo 选 `undefined` / omit）
 *
 * | 写法 | 语义 | 本层 |
 * |------|------|------|
 * | `{ xAxisData: [], seriesData: [] }` | 有图表结构，轴/序列为空 | ❌ 与 wire 整块缺失、`isHideChart` 混用；消费方仍会 `isEmpty([])` 二次判空 |
 * | `null` | 字段存在，值显式为空（对齐 wire `bar_charts: null`） | ❌ Vo 非 wire 镜像；易写成 `barChart ?? {}` 绕过 `isNoData` |
 * | `undefined` / omit | 当前状态下字段不适用，不应读取 | ✅ 与 `isNoData` 单一判别：`true` 只看 meta，`false` 才读 payload |
 *
 * ## 分层
 *
 * - **Dto / wire**：保留后端 `null`（协议层）。
 * - **chart 文件内 `normalize*`**：仅在 wire 块有效、且需字段清洗/重组时调用；无数据由 chart 内 `is*WireNoData` 短路。
 * - **轴类 `x_axis_data` / `series_data`**：与 wire 同形透传，Vo 仅做 camelCase 键名映射（见 {@link pickAxisChartWireVo}），不做逐元素 filter。
 * **Vo / Result**：`isNoData` 表达图表区空态；payload 仅在 `isNoData === false` 时出现。
 *
 * 带 `tableData` / `eventData` 的 chartType 同理：`isNoData === true` 时一并 omit，避免空数组伪装「有下载数据」。
 */

/**
 * wire 数组字段缺失：`null` / `undefined` / 非数组 / `[]`。
 *
 * 用于判定 ECharts 图表区是否无有效数据；各 chartType 私有 `is*WireNoData` 可复用。
 */
export function isMissingWireArray(value: unknown): boolean {
  return value == null || !Array.isArray(value) || value.length === 0;
}

/**
 * wire 是否为非空数组（与 {@link isMissingWireArray} 互斥）。
 *
 * 供 `is*WireNoData` 在判空后对 `.every` / `[0]` 做类型收窄。
 */
export function isNonEmptyWireArray<T>(
  value: readonly T[] | null | undefined,
): value is readonly T[] {
  return Array.isArray(value) && value.length > 0;
}

/** 轴类 wire 块是否含有效 `x_axis_data` 与 `series_data`（类型收窄供 Vo 键名映射）。 */
function isAxisChartWireDto(
  dto: IAnalysisAxisChartDataDto | null | undefined,
): dto is TAxisChartWireDto {
  if (dto == null || typeof dto !== 'object') {
    return false;
  }

  const { x_axis_data: xAxisDataWire, series_data: seriesDataWire } = dto;

  return (
    !isMissingWireArray(xAxisDataWire) &&
    !isMissingWireArray(seriesDataWire)
  );
}

/**
 * 轴类 wire → Vo + `isNoData`。
 *
 * 有数据时：`x_axis_data` / `series_data` **同形透传**，仅映射为 `xAxisData` / `seriesData`。
 * 无数据时返回 `{ isNoData: true }` 且不附带 `chartData`（omit / `undefined`），
 * 见「Vo 无数据态 · payload omit 约定」。
 *
 * ## 为何无独立 `normalizeAxisChartData`
 *
 * 历史上曾单独 normalize 并兜底空结构 / filter 数组元素；现改为：
 * - 无数据 → `isNoData` + omit，不在此返回 `{ xAxisData: [], seriesData: [] }`
 * - 有数据 → wire 数组原样挂到 Vo，仅改键名（类型注释已说明 series 与 wire 同形）
 */
export function pickAxisChartWireVo(
  dto: IAnalysisAxisChartDataDto | null | undefined,
): {
  chartData?: IAnalysisAxisChartDataVo;
  isNoData: boolean;
} {
  if (!isAxisChartWireDto(dto)) {
    return { isNoData: true };
  }

  const { x_axis_data: xAxisData, series_data: seriesData } = dto;

  return {
    chartData: { xAxisData, seriesData },
    isNoData: false,
  };
}

/** 轴类 wire → 薄 Vo；无数据时返回 `undefined`。 */
export function parseAxisChartDataVo(
  dto: IAnalysisAxisChartDataDto | null | undefined,
): IAnalysisAxisChartDataVo | undefined {
  return pickAxisChartWireVo(dto).chartData;
}

/** 图表区 `metrics` / `columnName`（对齐 model `chartData` 基座字段）。 */
export function pickChartDisplayMeta(
  dto: IAnalysisChartResponseMetaDto,
): IAnalysisChartDisplayMetaVo {
  const { metrics, column_name: columnNameWire } = dto;

  return {
    metrics: normalizeStringArray(metrics),
    columnName: normalizeColumnHeaderList(columnNameWire),
  };
}
